import { nanoid } from 'nanoid';
import {
    UndoRedoAction,
    UndoRedoState,
    CreateActionParams,
    DebounceConfig,
    HistoryEntry,
} from './types';

export class UndoRedoManager {
    private state: UndoRedoState;
    private listeners: Set<(state: UndoRedoState) => void> = new Set();
    private debounceTimers: Map<string, NodeJS.Timeout> = new Map();
    private pendingActions: Map<string, UndoRedoAction> = new Map();

    constructor(maxHistorySize: number = 50) {
        this.state = {
            history: [],
            currentIndex: -1,
            maxHistorySize,
            isUndoing: false,
            isRedoing: false,
        };
    }

    // Subscribe to state changes
    subscribe(listener: (state: UndoRedoState) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notify() {
        this.listeners.forEach((listener) => listener(this.state));
    }

    // Get current state
    getState(): UndoRedoState {
        return { ...this.state };
    }

    // Get history entries for UI display
    getHistoryEntries(): HistoryEntry[] {
        return this.state.history.map((action, index) => ({
            action,
            isActive: index === this.state.currentIndex,
            canUndoTo: index <= this.state.currentIndex,
            canRedoTo: index > this.state.currentIndex,
        }));
    }

    // Create and execute an action immediately
    executeAction(params: CreateActionParams): void {
        const action = this.createAction(params);
        this.addToHistory(action);

        try {
            action.execute();
        } catch (error) {
            console.error('Failed to execute action:', error);
            // Remove the failed action from history
            this.state.history.pop();
            this.state.currentIndex--;
        }

        this.notify();
    }

    // Add action to history with debouncing (without executing)
    recordActionDebounced(
        params: CreateActionParams,
        debounceConfig: DebounceConfig,
    ): void {
        const debounceKey =
            debounceConfig.groupByTarget && params.targetId
                ? `${params.category}-${params.targetId}`
                : params.category;

        // Clear existing timer
        const existingTimer = this.debounceTimers.get(debounceKey);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }

        // Create the action
        const action = this.createAction(params);

        // Store or merge the action
        if (this.pendingActions.has(debounceKey)) {
            // Merge with existing pending action
            const existingAction = this.pendingActions.get(debounceKey)!;
            const mergedAction = this.mergeActions(existingAction, action);
            this.pendingActions.set(debounceKey, mergedAction);
        } else {
            this.pendingActions.set(debounceKey, action);
        }

        // Set new timer (only add to history, don't execute)
        const timer = setTimeout(() => {
            const pendingAction = this.pendingActions.get(debounceKey);
            if (pendingAction) {
                this.addToHistory(pendingAction);
                this.pendingActions.delete(debounceKey);
                this.debounceTimers.delete(debounceKey);
                this.notify();
            }
        }, debounceConfig.delay);

        this.debounceTimers.set(debounceKey, timer);
    }

    // Add action to history without executing (for immediate UI updates)
    recordAction(params: CreateActionParams): void {
        const action = this.createAction(params);
        this.addToHistory(action);
        this.notify();
    }

    // Create and execute an action with debouncing
    executeActionDebounced(
        params: CreateActionParams,
        debounceConfig: DebounceConfig,
    ): void {
        const debounceKey =
            debounceConfig.groupByTarget && params.targetId
                ? `${params.category}-${params.targetId}`
                : params.category;

        // Clear existing timer
        const existingTimer = this.debounceTimers.get(debounceKey);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }

        // Update or create pending action
        const existingAction = this.pendingActions.get(debounceKey);
        if (existingAction) {
            // Merge changes
            const mergedAction = this.mergeActions(existingAction, params);
            this.pendingActions.set(debounceKey, mergedAction);
        } else {
            const newAction = this.createAction(params);
            this.pendingActions.set(debounceKey, newAction);
        }

        // Set new timer
        const timer = setTimeout(() => {
            const pendingAction = this.pendingActions.get(debounceKey);
            if (pendingAction) {
                this.addToHistory(pendingAction);
                try {
                    pendingAction.execute();
                } catch (error) {
                    console.error('Failed to execute debounced action:', error);
                    this.state.history.pop();
                    this.state.currentIndex--;
                }
                this.pendingActions.delete(debounceKey);
                this.debounceTimers.delete(debounceKey);
                this.notify();
            }
        }, debounceConfig.delay);

        this.debounceTimers.set(debounceKey, timer);
    }

    // Undo to a specific point in history
    undoTo(targetIndex: number): void {
        if (targetIndex > this.state.currentIndex || targetIndex < -1) {
            return;
        }

        this.state.isUndoing = true;
        this.notify();

        try {
            // Undo actions from current index down to target index
            for (let i = this.state.currentIndex; i > targetIndex; i--) {
                const action = this.state.history[i];
                action.undo();
            }
            this.state.currentIndex = targetIndex;
        } catch (error) {
            console.error('Failed to undo:', error);
        } finally {
            this.state.isUndoing = false;
            this.notify();
        }
    }

    // Redo to a specific point in history
    redoTo(targetIndex: number): void {
        if (
            targetIndex <= this.state.currentIndex ||
            targetIndex >= this.state.history.length
        ) {
            return;
        }

        this.state.isRedoing = true;
        this.notify();

        try {
            // Redo actions from current index up to target index
            for (let i = this.state.currentIndex + 1; i <= targetIndex; i++) {
                const action = this.state.history[i];
                action.execute();
            }
            this.state.currentIndex = targetIndex;
        } catch (error) {
            console.error('Failed to redo:', error);
        } finally {
            this.state.isRedoing = false;
            this.notify();
        }
    }

    // Standard undo (one step)
    undo(): void {
        if (this.canUndo()) {
            this.undoTo(this.state.currentIndex - 1);
        }
    }

    // Standard redo (one step)
    redo(): void {
        if (this.canRedo()) {
            this.redoTo(this.state.currentIndex + 1);
        }
    }

    // Check if undo is possible
    canUndo(): boolean {
        return this.state.currentIndex >= 0;
    }

    // Check if redo is possible
    canRedo(): boolean {
        return this.state.currentIndex < this.state.history.length - 1;
    }

    // Clear all history
    clear(): void {
        // Clear any pending debounced actions
        this.debounceTimers.forEach((timer) => clearTimeout(timer));
        this.debounceTimers.clear();
        this.pendingActions.clear();

        this.state = {
            ...this.state,
            history: [],
            currentIndex: -1,
        };
        this.notify();
    }

    // Private helper methods
    private createAction(params: CreateActionParams): UndoRedoAction {
        return {
            id: nanoid(),
            timestamp: Date.now(),
            category: params.category,
            description: params.description,
            targetId: params.targetId,
            targetName: params.targetName,
            changes: params.changes,
            execute: params.execute,
            undo: params.undo,
        };
    }

    private addToHistory(action: UndoRedoAction): void {
        // Remove any actions after current index (when adding new action after undo)
        if (this.state.currentIndex < this.state.history.length - 1) {
            this.state.history = this.state.history.slice(
                0,
                this.state.currentIndex + 1,
            );
        }

        // Add new action
        this.state.history.push(action);
        this.state.currentIndex = this.state.history.length - 1;

        // Trim history if it exceeds max size
        if (this.state.history.length > this.state.maxHistorySize) {
            this.state.history.shift();
            this.state.currentIndex--;
        }
    }

    private mergeActions(
        existingAction: UndoRedoAction,
        newParams: CreateActionParams,
    ): UndoRedoAction {
        // Merge property changes
        const mergedChanges = [...existingAction.changes];

        newParams.changes.forEach((newChange) => {
            const existingChangeIndex = mergedChanges.findIndex(
                (change) => change.path === newChange.path,
            );

            if (existingChangeIndex >= 0) {
                // Update existing change with new value, keep original old value
                mergedChanges[existingChangeIndex] = {
                    ...newChange,
                    oldValue: mergedChanges[existingChangeIndex].oldValue,
                };
            } else {
                // Add new change
                mergedChanges.push(newChange);
            }
        });

        // Create merged description
        const mergedDescription = this.createMergedDescription(
            existingAction,
            newParams,
        );

        return {
            ...existingAction,
            description: mergedDescription,
            changes: mergedChanges,
            execute: newParams.execute, // Use latest execute function
            // Keep original undo function since it should revert to the original state
        };
    }

    private createMergedDescription(
        existingAction: UndoRedoAction,
        newParams: CreateActionParams,
    ): string {
        // If it's the same type of change, keep the description simple
        if (existingAction.description.includes(newParams.description)) {
            return existingAction.description;
        }

        // For property changes, create a summary
        const uniqueProperties = new Set([
            ...existingAction.changes.map((c) => c.displayName),
            ...newParams.changes.map((c) => c.displayName),
        ]);

        if (uniqueProperties.size === 1) {
            return `Modified ${Array.from(uniqueProperties)[0]}`;
        } else if (uniqueProperties.size <= 3) {
            return `Modified ${Array.from(uniqueProperties).join(', ')}`;
        } else {
            return `Modified ${uniqueProperties.size} properties`;
        }
    }
}

// Global instance
export const undoRedoManager = new UndoRedoManager();
